import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useGQLQuery, useURLParameters } from 'hooks';
import {
  shiftModalDetailsQueryConfig,
  ShiftModalDetailsData,
} from 'modals/ShiftModal/ShiftModal.constants';
import { ShiftModalDataContext } from 'modals/ShiftModal/ShiftModalDataContext';
import { useShiftModalUIContext } from 'modals/ShiftModal/ShiftModal.ui.context';
import { ShiftModalOrderDetailsQuery } from 'types/graphql';
import { SSF_GLOBAL_CACHE_KEY } from 'services/ssfPersistenceService';
import { localPersistenceService } from 'services/localPersistenceService';
import { useLocation } from 'react-router-dom';

export {
  ShiftModalDataContext,
  useShiftModalDataContext,
} from 'modals/ShiftModal/ShiftModalDataContext';

const generateRefreshToken = () => Math.random().toString(36).substring(2);

interface IShiftModalDataProviderProps {
  locationId: number;
  shiftId?: string;
  isOpen?: boolean;
}

export const ShiftModalDataProvider: FC<PropsWithChildren<IShiftModalDataProviderProps>> = ({
  children,
  locationId,
  shiftId,
  isOpen = true,
}) => {
  const [refetchToken, setRefetchToken] = useState('');

  const value = useGQLQuery<
    ShiftModalOrderDetailsQuery,
    ShiftModalDetailsData,
    { id: string | undefined; communityId: number }
  >(shiftModalDetailsQueryConfig(locationId, shiftId));

  useEffect(() => {
    if (isOpen) {
      setRefetchToken(generateRefreshToken());
    }
  }, [isOpen]);

  useEffect(() => {
    if (typeof shiftId === 'string' && isOpen && refetchToken) value.fetch();
  }, [shiftId, refetchToken]); // eslint-disable-line

  return <ShiftModalDataContext.Provider value={value}>{children}</ShiftModalDataContext.Provider>;
};

export const ShiftModalGlobalCacheDataProvider: FC = ({ children }) => {
  const { shift_id, isOpen } = useShiftModalUIContext();
  const [{ shiftModalCommunityId }] = useURLParameters<{ shiftModalCommunityId?: string }>();

  const cacheJson = localPersistenceService.getItem(SSF_GLOBAL_CACHE_KEY());

  const facility_id = useMemo(() => {
    if (shiftModalCommunityId) return Number(shiftModalCommunityId);
    if (!cacheJson) return -1;
    const parsedJson = JSON.parse(cacheJson);
    return Number(parsedJson?.location?.value);
  }, [cacheJson, shiftModalCommunityId]);

  return (
    <ShiftModalDataProvider locationId={facility_id} shiftId={shift_id} isOpen={isOpen}>
      {children}
    </ShiftModalDataProvider>
  );
};

export const ShiftModalUrlDataProvider: FC<PropsWithChildren<{ shiftId?: string }>> = ({
  children,
  shiftId,
}) => {
  const { pathname } = useLocation();
  const locationId = Number(pathname.split('/')[3]);

  return (
    <ShiftModalDataProvider locationId={locationId} shiftId={shiftId} isOpen={true}>
      {children}
    </ShiftModalDataProvider>
  );
};
